import { z } from 'zod';

/**
 * Event types the Telegram bot can forward. Mirrors the names emitted on the
 * shared event bus (apps/api/src/lib/events.ts) so the same catalog drives
 * both webhooks and Telegram.
 */
export const TELEGRAM_EVENT_TYPES = [
  // ---------------------------------------------------------- selling
  // A sale rung up on the WEB register (/pos).
  'pos.sale.finalized',
  // A sale rung up on the offline COUNTER TILL (the desktop cashier), pushed up
  // when it syncs. Its own event, not `store.order.placed`: a counter sale is
  // every-few-minutes business, so it must never arrive at a shop that only ever
  // asked about online orders — you get it when you tick this box and not before.
  'store.order.counter',
  // Every new customer order, whichever door it came through: the web
  // storefront, the customer app, the portal and the AI assistant all funnel
  // through StorefrontCheckoutService.createOrderForParty, and a rep selling in
  // the field raises the same event from MandobFieldService.sell — the office
  // wants ONE alert for "a new order", with `source` saying where from.
  'store.order.placed',
  // ...and the same order narrowed to WHERE it came from. These are filters, not
  // extra events: nothing emits them. One order is delivered at most once — it
  // matches the umbrella above OR its own source box, so a shop that only wants
  // to hear about the phone unticks the umbrella and ticks the phone.
  'store.order.app',
  'store.order.website',
  'store.order.mandob',
  // ---------------------------------------------------------- money
  'invoice.posted',
  'payment.received',
  // An online order was paid by FIB. The money is already in the account — this
  // is the line that stops somebody collecting it again on delivery.
  'fib.order_paid',
  'hr.payroll.posted',
  // ---------------------------------------------------------- people & ops
  // Somebody clocked in or out (kiosk, own login, or a manager) — the owner
  // watching the door from their phone, lateness first. Never on by default:
  // a shop with twenty people would hear forty messages a day.
  'hr.attendance.punched',
  // A shopper signed up asking for a customer type (قرطاسية / مكاتب) and cannot
  // sign in until somebody answers.
  'customer.type_requested',
  'crm.lead.converted',
  'delivery.delivered',
  // An offline terminal honoured a coupon the server could not verify at sync
  // (or that is now worth a different amount). The discount stands — the
  // customer already paid it — so this alert IS the control: it names the code,
  // the cashier, the order and the amount within seconds of the push.
  'coupon.offline_unverified',
  // Somebody set a new exchange rate. Every posting from that moment converts
  // at the new number, so the office group hears it the second it happens —
  // with the old rate beside the new one.
  'fx.rate_changed',
] as const;
export type TelegramEventType = (typeof TELEGRAM_EVENT_TYPES)[number];

/**
 * `store.order.placed`'s payload `source` → the finer event id that same order
 * also satisfies. Shared so the notifier and the settings page can never drift
 * on what "an order from the phone" means.
 */
export const TELEGRAM_ORDER_SOURCE_EVENTS: Record<string, string> = {
  app: 'store.order.app',
  website: 'store.order.website',
  mandob: 'store.order.mandob',
};

/**
 * Bot config (settings key `telegram`). The bot token is a secret — API reads
 * never return it, only `botTokenSet` (whether one is stored).
 */
export interface TelegramConfig {
  enabled: boolean;
  botTokenSet: boolean;
  events: string[]; // event types enabled for Telegram delivery
  /** Two-way mode: staff can talk to the ERP assistant from Telegram. */
  assistantEnabled: boolean;
}

export const TelegramConfigUpdateSchema = z.object({
  enabled: z.boolean().optional(),
  /** Set/replace the bot token; null clears it; omit to leave unchanged. */
  botToken: z.string().min(10).max(200).nullable().optional(),
  events: z.array(z.string().max(64)).max(50).optional(),
  /** Turn the conversational assistant on/off; registers or clears the webhook. */
  assistantEnabled: z.boolean().optional(),
});
export type TelegramConfigUpdate = z.infer<typeof TelegramConfigUpdateSchema>;

/** A chat/group/channel to notify. */
export interface TelegramRecipient {
  id: string;
  label: string;
  chatId: string;
  isActive: boolean;
  events: string[] | null; // null/empty = all enabled events
  createdAt: string;
}

// Numeric chat id (users +, groups/channels −) or a public @channel username.
const CHAT_ID = z.string().min(1).max(64).regex(/^(-?\d+|@[A-Za-z0-9_]{3,})$/, 'invalid_chat_id');

export const TelegramRecipientCreateSchema = z.object({
  label: z.string().min(1).max(120),
  chatId: CHAT_ID,
  isActive: z.boolean().optional(),
  events: z.array(z.string().max(64)).max(50).nullable().optional(),
});
export type TelegramRecipientCreate = z.infer<typeof TelegramRecipientCreateSchema>;

export const TelegramRecipientUpdateSchema = z.object({
  label: z.string().min(1).max(120).optional(),
  chatId: CHAT_ID.optional(),
  isActive: z.boolean().optional(),
  events: z.array(z.string().max(64)).max(50).nullable().optional(),
});
export type TelegramRecipientUpdate = z.infer<typeof TelegramRecipientUpdateSchema>;

export const TelegramTestSchema = z.object({
  /** Target one chat; omit to send to all active recipients. */
  chatId: CHAT_ID.optional(),
});
export type TelegramTestInput = z.infer<typeof TelegramTestSchema>;

/** Result of a test send. */
export interface TelegramTestResult {
  sent: number;
  failed: number;
  error?: string | null;
}

// --------------------------------------------------------------- bot identity
/**
 * Outcome of asking Telegram who the stored token belongs to (`getMe`).
 * Returned as a 200 with a discriminated `status` rather than an HTTP error —
 * "no token yet" and "Telegram rejected this token" are page STATES the
 * settings screen renders, not request failures.
 */
export type TelegramBotStatus =
  | 'connected'    // getMe answered — `bot` is populated
  | 'no_token'     // nothing stored yet
  | 'rejected'     // Telegram refused the token (revoked/typo) — see `error`
  | 'unreachable'; // network/timeout talking to Telegram — see `error`

/** The bot behind the stored token. Public info only — never the token. */
export interface TelegramBotInfo {
  id: number;
  firstName: string;
  username: string | null;
  /** t.me link, or null when the bot somehow has no username. */
  link: string | null;
  canJoinGroups: boolean | null;
  /** False = privacy mode ON: in groups the bot only sees commands/replies. */
  canReadAllGroupMessages: boolean | null;
}

export interface TelegramBotIdentity {
  status: TelegramBotStatus;
  bot: TelegramBotInfo | null;
  /** Telegram's own `description` when it rejected us. Never contains the token. */
  error: string | null;
  /**
   * Host of the webhook currently registered for this bot, if any. Only the
   * HOST — a webhook path frequently embeds the bot token, so the full URL is
   * deliberately not exposed. A set webhook disables chat detection.
   */
  webhookHost: string | null;
  /** When this answer was fetched from Telegram (ISO). Reads may be cached. */
  checkedAt: string;
}

// ------------------------------------------------------------ chat discovery
export type TelegramChatType = 'private' | 'group' | 'supergroup' | 'channel';

/** A chat Telegram says the bot can currently see. */
export interface TelegramDetectedChat {
  /** Numeric chat id, as a string (users +, groups/channels −). */
  id: string;
  /** Group/channel title, or the person's name for a private chat. */
  title: string;
  username: string | null;
  type: TelegramChatType;
  /** True when a recipient with this chat already exists — don't offer Add. */
  alreadyAdded: boolean;
}

export type TelegramDiscoveryStatus =
  | 'ok'           // we talked to Telegram; `chats` may still be empty
  | 'no_token'     // nothing stored yet
  | 'rejected'     // Telegram refused the token
  | 'webhook_set'  // a webhook is registered, so getUpdates is unavailable
  | 'unreachable';

export interface TelegramChatDiscovery {
  status: TelegramDiscoveryStatus;
  chats: TelegramDetectedChat[];
  error: string | null;
  /** Host only — see {@link TelegramBotIdentity.webhookHost}. */
  webhookHost: string | null;
}
